// src/components/clients/config.tsx
import React from "react";
import {
  PersonAdd as PersonAddIcon,
  Tour as TourIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Textsms as TextsmsIcon,
  Phone as PhoneIcon,
  ListAlt as ListAltIcon,
  ContactMail as ContactMailIcon,
  Upload as UploadIcon,
  Receipt as ReceiptIcon,
  CardGiftcard as GiftCardIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Voicemail as VoicemailIcon,
} from "@mui/icons-material";
import type { ClientStatus, ClientPriority, ClientType } from "./types";

// Status configuration
export const statusConfig: Record<
  ClientStatus,
  { color: string; label: string; icon: React.ReactNode }
> = {
  new_client: {
    color: "#F59E0B",
    label: "New Client",
    icon: <PersonAddIcon fontSize="small" />,
  },
  touring: {
    color: "#0EA5E9",
    label: "Touring",
    icon: <TourIcon fontSize="small" />,
  },
  applied: {
    color: "#8B5CF6",
    label: "Applied",
    icon: <DescriptionIcon fontSize="small" />,
  },
  payment_received: {
    color: "#10B981",
    label: "Payment Received",
    icon: <PaymentIcon fontSize="small" />,
  },
  closed: {
    color: "#64748B",
    label: "Closed",
    icon: <CheckCircleIcon fontSize="small" />,
  },
  out_of_contact: {
    color: "#EF4444",
    label: "Archive",
    icon: <PhoneDisabledIcon fontSize="small" />,
  },
};

// Pipeline order for active clients
export const pipelineStatuses: ClientStatus[] = [
  "new_client",
  "touring",
  "applied",
  "payment_received",
];

// Status-specific quick actions
type StatusAction = { key: string; label: string; icon: React.ReactNode; activityType: string };

// PKT-0841 · Grant: New Client and Touring get the SAME four actions, same order.
// "Send List" was wrong: building a shortlist is not sending it. The stateless
// label is "Shortlist"; surfaces that know the client's list say
// Create List / Edit List via shortlistActionLabel().
const EARLY_STAGE_ACTIONS: StatusAction[] = [
  {
    key: "sent_text",
    label: "Send Text",
    icon: <TextsmsIcon fontSize="small" />,
    activityType: "sent_text",
  },
  {
    key: "call",
    label: "Call",
    icon: <PhoneIcon fontSize="small" />,
    activityType: "call_logged",
  },
  {
    key: "send_list",
    label: "Shortlist",
    icon: <ListAltIcon fontSize="small" />,
    activityType: "listing_shared",
  },
  {
    key: "send_guest_card",
    label: "Send Guest Card",
    icon: <ContactMailIcon fontSize="small" />,
    activityType: "guest_card_sent",
  },
];

export const statusActions: Record<ClientStatus, StatusAction[]> = {
  new_client: EARLY_STAGE_ACTIONS,
  touring: EARLY_STAGE_ACTIONS,
  applied: [
    {
      key: "upload_lease",
      label: "Upload Lease",
      icon: <UploadIcon fontSize="small" />,
      activityType: "lease_uploaded",
    },
    {
      key: "send_invoice",
      label: "Send Invoice",
      icon: <ReceiptIcon fontSize="small" />,
      activityType: "invoice_sent",
    },
  ],
  // PKT-0873 · "Send Gift Card" is HIDDEN until it is specced (PKT-0865
  // ruling 5, default accepted 2026-09-25). No surface had a handler for it —
  // ClientList's quick-action switch fell to `default: break` and the Messages
  // rail fell back to opening the record — so the button advertised an action
  // nothing performed. Restore the entry here when a real flow exists; every
  // surface reads this one list.
  payment_received: [],
  closed: [],
  out_of_contact: [
    {
      key: "sent_text",
      label: "Send Text",
      icon: <TextsmsIcon fontSize="small" />,
      activityType: "sent_text",
    },
    {
      key: "call",
      label: "Call",
      icon: <PhoneIcon fontSize="small" />,
      activityType: "call_logged",
    },
  ],
};

/**
 * PKT-0841 · what each status EMPHASISES, unchanged from before the four-action
 * ruling. The N1 queue button (nextActionFor), the rail's filled button and a
 * client card's two inline buttons read these, so giving New Client and Touring
 * the same list does not change what either stage recommends.
 * Statuses not listed emphasise the first two of their own list, as before.
 */
export const statusPrimaryKeys: Partial<Record<ClientStatus, string[]>> = {
  new_client: ["sent_text", "call"],
  touring: ["send_list", "send_guest_card"],
};

/** The status's actions with its emphasised ones first, in their old order. */
export function actionsByEmphasis(status: ClientStatus): StatusAction[] {
  const acts = statusActions[status] || [];
  const primary = statusPrimaryKeys[status];
  if (!primary) return acts;
  const first = primary
    .map((key) => acts.find((a) => a.key === key))
    .filter((a): a is StatusAction => Boolean(a));
  return [...first, ...acts.filter((a) => !primary.includes(a.key))];
}

/** Create List / Edit List when the list is known; "Shortlist" when it is not. */
export function shortlistActionLabel(hasItems: boolean | null): string {
  if (hasItems === null) return "Shortlist";
  return hasItems ? "Edit List" : "Create List";
}

// All activity types for logging
export const allActivityTypes = [
  {
    key: "call_logged",
    label: "Logged Call",
    icon: <PhoneIcon fontSize="small" />,
  },
  {
    key: "sent_text",
    label: "Sent Text",
    icon: <TextsmsIcon fontSize="small" />,
  },
  {
    key: "left_voicemail",
    label: "Left Voicemail",
    icon: <VoicemailIcon fontSize="small" />,
  },
  {
    key: "email_sent",
    label: "Sent Email",
    icon: <EmailIcon fontSize="small" />,
  },
  {
    key: "meeting_scheduled",
    label: "Scheduled Meeting",
    icon: <CalendarIcon fontSize="small" />,
  },
  {
    key: "tour_scheduled",
    label: "Scheduled Tour",
    icon: <TourIcon fontSize="small" />,
  },
  {
    key: "listing_shared",
    label: "Shared Listings",
    icon: <ListAltIcon fontSize="small" />,
  },
  {
    key: "guest_card_sent",
    label: "Sent Guest Card",
    icon: <ContactMailIcon fontSize="small" />,
  },
  {
    key: "invoice_sent",
    label: "Sent Invoice",
    icon: <ReceiptIcon fontSize="small" />,
  },
  {
    key: "lease_uploaded",
    label: "Uploaded Lease",
    icon: <UploadIcon fontSize="small" />,
  },
  {
    key: "gift_card_sent",
    label: "Sent Gift Card",
    icon: <GiftCardIcon fontSize="small" />,
  },
];

// Task type options
export const taskTypeOptions = [
  { value: "follow_up", label: "Follow Up" },
  { value: "scheduled_tour", label: "Scheduled Tour" },
  { value: "touring", label: "Touring" },
  { value: "application_submitted", label: "Application Submitted" },
  { value: "invoice_sent", label: "Invoice Sent" },
  { value: "payment_received", label: "Payment Received" },
  { value: "lease_signed", label: "Lease Signed" },
  { value: "move_in", label: "Move In" },
  { value: "custom", label: "Custom" },
];

// Task cadence channel config (PKT-0245 · migration 041).
// `activityType` is the contact-activity row mirrored on complete (feeds the
// client-level last-touch); null = no contact mirror for that channel.
export const channelConfig: Record<
  string,
  { label: string; icon: React.ReactElement; activityType: string | null }
> = {
  text: { label: "Text", icon: <TextsmsIcon fontSize="small" />, activityType: "sent_text" },
  call: { label: "Call", icon: <PhoneIcon fontSize="small" />, activityType: "call_logged" },
  email: { label: "Email", icon: <EmailIcon fontSize="small" />, activityType: "email_sent" },
  tour: { label: "Tour", icon: <TourIcon fontSize="small" />, activityType: "tour_scheduled" },
  task: { label: "Task", icon: <ListAltIcon fontSize="small" />, activityType: null },
};

// Priority configuration
export const priorityConfig: Record<
  ClientPriority,
  { color: string; label: string }
> = {
  urgent: { color: "#EF4444", label: "Urgent" },
  high: { color: "#F97316", label: "High" },
  normal: { color: "#0EA5E9", label: "Normal" },
  low: { color: "#64748B", label: "Low" },
};

// Client type configuration
export const typeConfig: Record<ClientType, { color: string; label: string }> =
  {
    renter: { color: "#6366F1", label: "Renter" },
    buyer: { color: "#10B981", label: "Buyer" },
    landlord: { color: "#F97316", label: "Landlord" },
    investor: { color: "#EC4899", label: "Investor" },
  };
